import { useCallback, useEffect, useRef, useState } from 'react';

// Keeps the screen awake while cooking (Screen Wake Lock API). Used by cook mode on the recipe
// screen and by step-by-step cooking.
//
// Bug fixed 2026-10-11: the old version released the lock in an effect cleanup that re-ran every
// time `isActive` changed, so turning cook mode on released the lock it had just taken, and the
// 'release' event switched cook mode straight back off. The lock is now released only when the
// user turns it off or the screen unmounts.
export function useWakeLock() {
  const [isSupported] = useState(() => typeof navigator !== 'undefined' && 'wakeLock' in navigator);
  const [isActive, setIsActive] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);
  // True when the browser refused the lock (e.g. low battery, not a secure context). Cook mode still
  // shows as on so the button responds, but the screen may sleep. Kept from the original behaviour.
  const [simulatedMode, setSimulatedMode] = useState(false);
  const wakeLockRef = useRef(null);
  // Whether the user wants cook mode on, so the lock can be re-taken when they come back to the tab
  // (browsers drop it automatically when the page is hidden).
  const wantedRef = useRef(false);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return false;
    setHasAttempted(true);
    wantedRef.current = true;
    try {
      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;
      // The browser drops the lock when the tab is hidden; that isn't the user turning cook mode off,
      // so leave isActive alone unless they've asked for it off.
      lock.addEventListener('release', () => {
        if (wakeLockRef.current === lock) wakeLockRef.current = null;
        if (!wantedRef.current) setIsActive(false);
      });
      setSimulatedMode(false);
      setIsActive(true);
      return true;
    } catch (err) {
      setSimulatedMode(true);
      setIsActive(true);
      return true;
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    wantedRef.current = false;
    setIsActive(false);
    setSimulatedMode(false);
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    if (lock) {
      try {
        await lock.release();
      } catch (_) {
        // Already released.
      }
    }
  }, []);

  // Re-take the lock when the user returns to the tab, if cook mode is still on.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (wantedRef.current && document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [requestWakeLock]);

  // Release only when the screen using this hook goes away.
  useEffect(() => () => {
    wantedRef.current = false;
    const lock = wakeLockRef.current;
    wakeLockRef.current = null;
    if (lock) lock.release().catch(() => {});
  }, []);

  return {
    isSupported,
    isActive,
    hasAttempted,
    requestWakeLock,
    releaseWakeLock,
    simulatedMode,
    setSimulatedMode,
  };
}
